import className from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import {
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../../models/catalogue";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { PLCInfoPanel } from "../PLCInfoPanel";
import {
    IPLCMattressFeelOptionSelector,
    IPLCOptionPanel,
    IPLCProductCategorySelector,
} from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setOptionValue } from "../reducers";
import { ISelectedCategories } from "../reducers.interfaces";
import { selectedCategoriesSelector } from "../selectors";
import { getOptionValueByIdx } from "../utils";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-mattress-feel-option-selector";

const FEELS = [
    {
        label: t`Ultra Soft`,
    },
    {
        label: t`Soft`,
    },
    {
        label: t`Medium`,
    },
    {
        label: t`Firm`,
    },
    {
        label: t`Ultra Firm`,
    },
];

const FEEL_ATTR = "option_feel";

/**
 * Check if selecting this option will result in a perfect product match or not. If
 * not, the option should be visually disabled (but still actually selectable).
 */
const isOptionDisabled = (
    option: IOptionSingleValue,
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    rootProducts: readonly IProduct[],
    selectedCategories: ISelectedCategories,
    optionValues: IOptionValues,
): boolean => {
    const futureOptionValues = {
        ...optionValues,
        [FEEL_ATTR]: option,
    };
    const result = getMostSimilarProduct({
        categorySelectors,
        optionPanels,
        rootProducts,
        selectedCategories,
        optionValues: futureOptionValues,
        previouslySelectedCategories: selectedCategories,
        previouslySelectedOptionValues: optionValues,
    });
    return !result || result.distance > 0;
};

export const PLCMattressFeelOptionSelector = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        selector: IPLCMattressFeelOptionSelector;
    }>,
): React.ReactNode => {
    const state = useAppSelector((rootState) => rootState.configurator);
    const dispatch = useAppDispatch();

    const rootProducts = state.entities.rootProducts;
    const selectedCategories = selectedCategoriesSelector(state);
    const optionValues = state.ui.optionValues;
    const value = getOptionValueByIdx(state.ui.optionValues[FEEL_ATTR], 0);

    const handleChange = (optionLabel: string) => {
        dispatch(
            setOptionValue({
                namespace: "",
                code: FEEL_ATTR,
                index: 0,
                totalNumValues: 1,
                value: optionLabel,
            }),
        );
    };

    const groupID = props.selector.id;

    return (
        <fieldset className={BEM_NAME}>
            <legend>
                <span className="ada-screenreader-only">
                    {t`Mattress Feel`}
                </span>
            </legend>
            <ul>
                {FEELS.map((opt) => {
                    const optionID = `${groupID}-${opt.label}`
                        .replace(/[^a-zA-Z0-9-]/gi, "")
                        .toLowerCase();
                    const isDisabled = isOptionDisabled(
                        opt.label,
                        props.categorySelectors,
                        props.optionPanels,
                        rootProducts,
                        selectedCategories,
                        optionValues,
                    );
                    const isChecked = value === opt.label;
                    const inputBorderClasses = className({
                        [`${BEM_NAME}__input-border`]: true,
                        [`${BEM_NAME}__input-border--checked`]: isChecked,
                    });
                    const inputClass = className({
                        [`${BEM_NAME}__input`]: true,
                        [`${BEM_NAME}__input--faux-disabled`]: isDisabled,
                    });
                    const labelClass = className({
                        [`${BEM_NAME}__label`]: true,
                        [`${BEM_NAME}__label--faux-disabled`]: isDisabled,
                    });
                    const textClass = className({
                        [`${BEM_NAME}__text`]: true,
                        [`${BEM_NAME}__text--checked`]: isChecked,
                    });
                    return (
                        <li key={opt.label} className={`${BEM_NAME}__option`}>
                            <label
                                htmlFor={optionID}
                                className={labelClass}
                                aria-selected={false}
                            >
                                <span className={inputBorderClasses}>
                                    <input
                                        type="radio"
                                        className={inputClass}
                                        id={optionID}
                                        name={groupID}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                handleChange(opt.label);
                                            }
                                        }}
                                        checked={value === opt.label}
                                        value={opt.label}
                                    />
                                </span>
                                <span className={textClass}>{opt.label}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
            <PLCInfoPanel
                buttonText={t`Which feel is right for me?`}
                content={props.selector.value.help_content}
            />
        </fieldset>
    );
};
